import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
    ArrowLeft, Users, CreditCard, UserCheck, DollarSign, BookOpen, 
    CheckCircle, XCircle, Clock, Plus, Loader2, RefreshCw, Search,
    Shield, UserPlus, Settings, LogOut, Star, Calendar, Gift, Video, Mail,
    Ban, PauseCircle, PlayCircle, Ticket, Copy, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';

const AdminDashboard = ({ onBack, onNavigate }) => {
    const [sessionId, setSessionId] = useState(localStorage.getItem('eskrybe_admin_session'));
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [users, setUsers] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [payoutRequests, setPayoutRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('overview');

    // Login form state
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [loginLoading, setLoginLoading] = useState(false);
    
    // Create account modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createAccountData, setCreateAccountData] = useState({
        email: '',
        name: '',
        password: '',
        is_demo: false,
        demo_duration_days: 14,
        credits: 25
    });
    const [createLoading, setCreateLoading] = useState(false);
    
    // Edit demo modal state
    const [editDemoUser, setEditDemoUser] = useState(null);
    const [editDemoDuration, setEditDemoDuration] = useState('14');
    
    // Promo codes state
    const [promoCodes, setPromoCodes] = useState([]);
    const [promoStats, setPromoStats] = useState({ total: 0, active: 0, used: 0 });
    const [promoFilter, setPromoFilter] = useState('all');
    const [promoLoading, setPromoLoading] = useState(false);
    const [generatePromoData, setGeneratePromoData] = useState({
        template_id: 'author_25_3mo',
        quantity: 1,
        custom_code: '',
        notes: ''
    });
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    
    const PROMO_TEMPLATES = {
        "author_25_3mo": { name: "Author 25% Off (3 months)", description: "25% off Author + 200 credits/mo" },
        "author_50_3mo": { name: "Author 50% Off (3 months)", description: "50% off Author + 200 credits/mo" },
        "author_100_6mo": { name: "Author FREE (6 months)", description: "FREE Author + 200 credits/mo" },
        "studio_100_3mo": { name: "Studio FREE (3 months)", description: "FREE Studio + 400 credits/mo" },
        "studio_100_6mo": { name: "Studio FREE (6 months)", description: "FREE Studio + 400 credits/mo" }
    };
    
    // Affiliate message modal state
    const [messageAffiliate, setMessageAffiliate] = useState(null);
    const [messageSubject, setMessageSubject] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [messageSending, setMessageSending] = useState(false);
    
    // Bulk selection state
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);

    useEffect(() => {
        if (sessionId) {
            verifySession();
        } else {
            setLoading(false);
        }
    }, [sessionId]);

    const verifySession = async () => {
        try {
            const response = await adminApi.getDashboard(sessionId);
            setDashboardData(response.data);
            setIsAuthenticated(true);
            loadData();
        } catch (error) {
            localStorage.removeItem('eskrybe_admin_session');
            setSessionId(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    const loadData = async () => {
        try {
            const [usersRes, affiliatesRes, payoutsRes] = await Promise.all([
                adminApi.getUsers(sessionId),
                adminApi.getAffiliates(sessionId),
                adminApi.getPayoutRequests(sessionId, 'pending')
            ]);
            setUsers(usersRes.data.users || []);
            setAffiliates(affiliatesRes.data.affiliates || []);
            setPayoutRequests(payoutsRes.data.requests || []);
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };

    // Promo code functions
    const loadPromoCodes = async (filter = 'all') => {
        setPromoLoading(true);
        try {
            const token = localStorage.getItem('eskrybe_token');
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/promo/list?status=${filter}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
                setPromoCodes(data.codes || []);
                setPromoStats(data.stats || { total: 0, active: 0, used: 0 });
            }
        } catch (error) {
            console.error('Failed to load promo codes:', error);
        } finally {
            setPromoLoading(false);
        }
    };

    const handleGeneratePromo = async () => {
        try {
            const token = localStorage.getItem('eskrybe_token');
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/promo/generate`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(generatePromoData)
            });
            const data = await response.json();
            if (response.ok) {
                toast.success(`Generated ${data.codes.length} promo code(s)`);
                setShowGenerateModal(false);
                setGeneratePromoData({ template_id: 'author_25_3mo', quantity: 1, custom_code: '', notes: '' });
                loadPromoCodes(promoFilter);
            } else {
                toast.error(data.detail || 'Failed to generate promo code');
            }
        } catch (error) {
            toast.error('Failed to generate promo code');
        }
    };

    const handleDeactivatePromo = async (code) => {
        if (!window.confirm(`Deactivate promo code ${code}?`)) return;
        try {
            const token = localStorage.getItem('eskrybe_token');
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/promo/deactivate/${code}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                toast.success('Promo code deactivated');
                loadPromoCodes(promoFilter);
            }
        } catch (error) {
            toast.error('Failed to deactivate promo code');
        }
    };

    const handleDeletePromo = async (code) => {
        if (!window.confirm(`Delete promo code ${code}? This cannot be undone.`)) return;
        try {
            const token = localStorage.getItem('eskrybe_token');
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/promo/delete/${code}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                toast.success('Promo code deleted');
                loadPromoCodes(promoFilter);
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Failed to delete promo code');
            }
        } catch (error) {
            toast.error('Failed to delete promo code');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        try {
            const response = await adminApi.login(loginData);
            const newSessionId = response.data.session_id;
            localStorage.setItem('eskrybe_admin_session', newSessionId);
            setSessionId(newSessionId);
            
            // Directly fetch dashboard with the new session ID instead of relying on state
            const dashboardResponse = await adminApi.getDashboard(newSessionId);
            setDashboardData(dashboardResponse.data);
            setIsAuthenticated(true);
            
            // Load additional data
            const [usersRes, affiliatesRes, payoutsRes] = await Promise.all([
                adminApi.getUsers(newSessionId),
                adminApi.getAffiliates(newSessionId),
                adminApi.getPayoutRequests(newSessionId, 'pending')
            ]);
            setUsers(usersRes.data.users || []);
            setAffiliates(affiliatesRes.data.affiliates || []);
            setPayoutRequests(payoutsRes.data.requests || []);
            
            toast.success('Admin login successful');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await adminApi.logout(sessionId);
        } catch (error) {
            console.error('Logout error:', error);
        }
        localStorage.removeItem('eskrybe_admin_session');
        setSessionId(null);
        setIsAuthenticated(false);
        setDashboardData(null);
    };

    const handleApproveAffiliate = async (affiliateId) => {
        try {
            await adminApi.updateAffiliate(sessionId, affiliateId, { is_approved: true });
            toast.success('Affiliate approved and notification email sent');
            loadData();
        } catch (error) {
            toast.error('Failed to approve affiliate');
        }
    };
    
    const handleRejectAffiliate = async (affiliateId) => {
        if (!window.confirm('Are you sure you want to reject this affiliate application? An email notification will be sent.')) {
            return;
        }
        try {
            await adminApi.updateAffiliate(sessionId, affiliateId, { is_approved: false });
            toast.success('Affiliate application rejected and notification email sent');
            loadData();
        } catch (error) {
            toast.error('Failed to reject affiliate');
        }
    };
    
    const handleRevokeAffiliate = async (affiliateId) => {
        if (!window.confirm('Are you sure you want to revoke this affiliate? They will no longer earn commissions. An email notification will be sent.')) {
            return;
        }
        try {
            await adminApi.updateAffiliate(sessionId, affiliateId, { is_active: false });
            toast.success('Affiliate revoked and notification email sent');
            loadData();
        } catch (error) {
            toast.error('Failed to revoke affiliate');
        }
    };
    
    const handleSendAffiliateMessage = async () => {
        if (!messageSubject.trim() || !messageContent.trim()) {
            toast.error('Please enter both subject and message');
            return;
        }
        setMessageSending(true);
        try {
            await adminApi.messageAffiliate(sessionId, messageAffiliate.id, messageSubject, messageContent);
            toast.success('Message sent successfully');
            setMessageAffiliate(null);
            setMessageSubject('');
            setMessageContent('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send message');
        } finally {
            setMessageSending(false);
        }
    };

    const handleApprovePayout = async (requestId) => {
        try {
            await adminApi.approvePayoutRequest(sessionId, requestId);
            toast.success('Payout approved');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve payout');
        }
    };

    const handleRejectPayout = async (requestId) => {
        const reason = prompt('Enter rejection reason:');
        if (reason === null) return;
        try {
            await adminApi.rejectPayoutRequest(sessionId, requestId, reason);
            toast.success('Payout rejected');
            loadData();
        } catch (error) {
            toast.error('Failed to reject payout');
        }
    };

    const handleCloseAccount = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to close the account for ${userName}?\n\nThis will:\n• Schedule account for permanent deletion in 60 days\n• Send notification email to the user\n\nThis action can be reversed by reactivating the account.`)) {
            return;
        }
        try {
            await adminApi.closeAccount(sessionId, userId);
            toast.success('Account closed - scheduled for deletion in 60 days');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to close account');
        }
    };

    const handleSetIdle = async (userId, userName) => {
        if (!window.confirm(`Set account to Idle for ${userName}?\n\nThis will:\n• Mark account as idle (payment issue)\n• Send weekly reminder emails\n• Content retained for 90 days\n\nThis action can be reversed by reactivating the account.`)) {
            return;
        }
        try {
            await adminApi.setAccountIdle(sessionId, userId);
            toast.success('Account set to idle - weekly reminders enabled');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to set account idle');
        }
    };

    const handleReactivateAccount = async (userId, userName) => {
        if (!window.confirm(`Reactivate account for ${userName}?`)) {
            return;
        }
        try {
            await adminApi.reactivateAccount(sessionId, userId);
            toast.success('Account reactivated successfully');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reactivate account');
        }
    };

    // Bulk selection handlers
    const handleSelectUser = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(u => u.id));
        }
    };

    const handleBulkClose = async () => {
        if (selectedUsers.length === 0) return;
        if (!window.confirm(`Are you sure you want to close ${selectedUsers.length} account(s)?\n\nThis will schedule them for deletion in 60 days.`)) {
            return;
        }
        setBulkActionLoading(true);
        let successCount = 0;
        let failCount = 0;
        
        for (const userId of selectedUsers) {
            try {
                await adminApi.closeAccount(sessionId, userId);
                successCount++;
            } catch (error) {
                failCount++;
            }
        }
        
        setBulkActionLoading(false);
        setSelectedUsers([]);
        
        if (successCount > 0) {
            toast.success(`${successCount} account(s) closed successfully`);
        }
        if (failCount > 0) {
            toast.error(`Failed to close ${failCount} account(s)`);
        }
        loadData();
    };

    const handleBulkIdle = async () => {
        if (selectedUsers.length === 0) return;
        if (!window.confirm(`Set ${selectedUsers.length} account(s) to Idle status?\n\nThey will receive weekly reminder emails.`)) {
            return;
        }
        setBulkActionLoading(true);
        let successCount = 0;
        let failCount = 0;
        
        for (const userId of selectedUsers) {
            try {
                await adminApi.setAccountIdle(sessionId, userId);
                successCount++;
            } catch (error) {
                failCount++;
            }
        }
        
        setBulkActionLoading(false);
        setSelectedUsers([]);
        
        if (successCount > 0) {
            toast.success(`${successCount} account(s) set to idle`);
        }
        if (failCount > 0) {
            toast.error(`Failed to set ${failCount} account(s) to idle`);
        }
        loadData();
    };

    const handleBulkReactivate = async () => {
        if (selectedUsers.length === 0) return;
        if (!window.confirm(`Reactivate ${selectedUsers.length} account(s)?`)) {
            return;
        }
        setBulkActionLoading(true);
        let successCount = 0;
        let failCount = 0;
        
        for (const userId of selectedUsers) {
            try {
                await adminApi.reactivateAccount(sessionId, userId);
                successCount++;
            } catch (error) {
                failCount++;
            }
        }
        
        setBulkActionLoading(false);
        setSelectedUsers([]);
        
        if (successCount > 0) {
            toast.success(`${successCount} account(s) reactivated`);
        }
        if (failCount > 0) {
            toast.error(`Failed to reactivate ${failCount} account(s)`);
        }
        loadData();
    };

    const handleAddCredits = async (userId) => {
        const credits = prompt('Enter credits to add:');
        if (!credits || isNaN(parseInt(credits))) return;
        try {
            await adminApi.addCredits(sessionId, userId, parseInt(credits));
            toast.success(`Added ${credits} credits`);
            loadData();
        } catch (error) {
            toast.error('Failed to add credits');
        }
    };

    const handleToggleDemo = async (userId, currentIsDemo) => {
        if (currentIsDemo) {
            // Revoking demo status
            if (!window.confirm('Revoke demo access for this user? They will keep their work but lose premium features.')) return;
            try {
                await adminApi.setDemoStatus(sessionId, userId, false);
                toast.success('Demo access revoked');
                loadData();
            } catch (error) {
                toast.error('Failed to revoke demo status');
            }
        } else {
            // Show duration selection modal - handled by DemoModal component
            setShowDemoModal(userId);
        }
    };

    const handleGrantDemo = async (userId, durationDays) => {
        try {
            await adminApi.setDemoStatus(sessionId, userId, true, durationDays);
            toast.success(`Demo access granted for ${durationDays} days`);
            setShowDemoModal(null);
            loadData();
        } catch (error) {
            toast.error('Failed to grant demo status');
        }
    };
    
    // Edit demo duration for existing demo user
    const handleEditDemo = (user) => {
        setEditDemoUser(user);
        setEditDemoDuration(user.demo_duration_days?.toString() || '14');
    };
    
    const handleUpdateDemoDuration = async () => {
        if (!editDemoUser) return;
        try {
            await adminApi.setDemoStatus(sessionId, editDemoUser.id, true, parseInt(editDemoDuration));
            toast.success(`Demo extended to ${editDemoDuration} days from today`);
            setEditDemoUser(null);
            loadData();
        } catch (error) {
            toast.error('Failed to update demo duration');
        }
    };
    
    // Create new account (regular or demo)
    const handleCreateAccount = async () => {
        if (!createAccountData.email || !createAccountData.name || !createAccountData.password) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        setCreateLoading(true);
        try {
            if (createAccountData.is_demo) {
                // Create demo account
                await adminApi.createDemoAccount(sessionId, {
                    email: createAccountData.email,
                    name: createAccountData.name,
                    password: createAccountData.password,
                    credits: createAccountData.credits,
                    expires_in_days: createAccountData.demo_duration_days
                });
                toast.success(`Demo account created for ${createAccountData.email}`);
            } else {
                // Create regular account via admin endpoint
                await adminApi.createUser(sessionId, {
                    email: createAccountData.email,
                    name: createAccountData.name,
                    password: createAccountData.password,
                    credits: createAccountData.credits
                });
                toast.success(`Account created for ${createAccountData.email}`);
            }
            setShowCreateModal(false);
            setCreateAccountData({
                email: '',
                name: '',
                password: '',
                is_demo: false,
                demo_duration_days: 14,
                credits: 25
            });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create account');
        } finally {
            setCreateLoading(false);
        }
    };

    const [showDemoModal, setShowDemoModal] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState('14');

    // Helper to check if demo is expired
    const isDemoExpired = (user) => {
        if (!user.is_demo || !user.demo_expires_at) return false;
        return new Date(user.demo_expires_at) < new Date();
    };

    // Helper to format expiration date
    const formatExpiration = (expiresAt) => {
        if (!expiresAt) return '';
        const date = new Date(expiresAt);
        const now = new Date();
        const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return 'Expired';
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return '1 day left';
        return `${diffDays} days left`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    // Login form
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-background">
                <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
                    <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                        <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </button>
                        <div className="flex items-center gap-2">
                            <Shield className="h-5 w-5 text-accent" />
                            <span className="font-semibold">Admin Portal</span>
                        </div>
                        <div className="w-16" />
                    </div>
                </header>

                <div className="max-w-md mx-auto mt-20 px-6">
                    <Card>
                        <CardHeader className="text-center">
                            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                                <Shield className="h-8 w-8 text-accent" />
                            </div>
                            <CardTitle>Admin Login</CardTitle>
                            <CardDescription>Enter your admin credentials to continue</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleLogin} className="space-y-4">
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={loginData.email}
                                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                        required
                                        data-testid="admin-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={loginData.password}
                                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                        required
                                        data-testid="admin-password-input"
                                    />
                                </div>
                                <Button type="submit" className="w-full" disabled={loginLoading} data-testid="admin-login-btn">
                                    {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Login
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Admin Dashboard
    const filteredUsers = users.filter(u => 
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const pendingAffiliates = affiliates.filter(a => !a.is_approved);

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" />
                        Back to App
                    </button>
                    <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-accent" />
                        <span className="font-semibold">Admin Dashboard</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {onNavigate && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => onNavigate('video-manager')}
                                data-testid="video-manager-btn"
                            >
                                <Video className="h-4 w-4 mr-1" />
                                Videos
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={loadData}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleLogout}>
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats Overview */}
                {dashboardData && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-500/10 rounded-lg">
                                        <Users className="h-5 w-5 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{dashboardData.users?.total || 0}</p>
                                        <p className="text-xs text-muted-foreground">Total Users</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-amber-500/10 rounded-lg">
                                        <Star className="h-5 w-5 text-amber-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{dashboardData.users?.active_demo || 0}</p>
                                        <p className="text-xs text-muted-foreground">Active Demos</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-500/10 rounded-lg">
                                        <CreditCard className="h-5 w-5 text-green-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{dashboardData.users?.paid || 0}</p>
                                        <p className="text-xs text-muted-foreground">Paid Users</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-500/10 rounded-lg">
                                        <BookOpen className="h-5 w-5 text-purple-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{dashboardData.content?.total_books || 0}</p>
                                        <p className="text-xs text-muted-foreground">Total Books</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-orange-500/10 rounded-lg">
                                        <UserCheck className="h-5 w-5 text-orange-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{dashboardData.affiliates?.active || 0}</p>
                                        <p className="text-xs text-muted-foreground">Active Affiliates</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Alerts */}
                {(pendingAffiliates.length > 0 || payoutRequests.length > 0) && (
                    <div className="mb-6 space-y-3">
                        {pendingAffiliates.length > 0 && (
                            <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                <Clock className="h-5 w-5 text-yellow-500" />
                                <span className="text-sm">{pendingAffiliates.length} affiliate application(s) pending approval</span>
                                <Button size="sm" variant="outline" onClick={() => setActiveTab('affiliates')}>Review</Button>
                            </div>
                        )}
                        {payoutRequests.length > 0 && (
                            <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                <DollarSign className="h-5 w-5 text-blue-500" />
                                <span className="text-sm">{payoutRequests.length} payout request(s) pending</span>
                                <Button size="sm" variant="outline" onClick={() => setActiveTab('payouts')}>Review</Button>
                            </div>
                        )}
                    </div>
                )}

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="users">Users</TabsTrigger>
                        <TabsTrigger value="affiliates">Affiliates</TabsTrigger>
                        <TabsTrigger value="promo" onClick={() => loadPromoCodes()}>Promo Codes</TabsTrigger>
                        <TabsTrigger value="payouts">Payouts</TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Recent Signups */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Recent Signups (7 days)</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-3xl font-bold text-accent">{dashboardData?.users?.recent_signups_7d || 0}</p>
                                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                                            <span>Free</span>
                                            <span className="font-medium">{dashboardData?.users?.by_tier?.free || 0}</span>
                                        </div>
                                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                                            <span>Writer</span>
                                            <span className="font-medium">{dashboardData?.users?.by_tier?.writer || 0}</span>
                                        </div>
                                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                                            <span>Author</span>
                                            <span className="font-medium">{dashboardData?.users?.by_tier?.author || 0}</span>
                                        </div>
                                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                                            <span>Studio</span>
                                            <span className="font-medium">{dashboardData?.users?.by_tier?.studio || 0}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Affiliate Summary */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Affiliate Program</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                                            <span>Total Affiliates</span>
                                            <Badge variant="secondary">{dashboardData?.affiliates?.total || 0}</Badge>
                                        </div>
                                        <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                                            <span>Pending Approval</span>
                                            <Badge variant={pendingAffiliates.length > 0 ? "destructive" : "secondary"}>
                                                {dashboardData?.affiliates?.pending_approval || 0}
                                            </Badge>
                                        </div>
                                        <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                                            <span>Pending Payouts</span>
                                            <Badge variant={payoutRequests.length > 0 ? "destructive" : "secondary"}>
                                                {dashboardData?.affiliates?.pending_payouts || 0}
                                            </Badge>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Users Tab */}
                    <TabsContent value="users">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <CardTitle>User Management</CardTitle>
                                        <Button 
                                            size="sm" 
                                            onClick={() => setShowCreateModal(true)}
                                            data-testid="create-account-btn"
                                        >
                                            <UserPlus className="h-4 w-4 mr-1" />
                                            Create Account
                                        </Button>
                                    </div>
                                    <div className="relative w-64">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search users..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-9"
                                        />
                                    </div>
                                </div>
                                
                                {/* Bulk Actions Bar */}
                                {selectedUsers.length > 0 && (
                                    <div className="mt-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                                        <span className="text-sm font-medium">
                                            {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <Button 
                                                size="sm" 
                                                variant="outline"
                                                className="text-green-600 border-green-600 hover:bg-green-50"
                                                onClick={handleBulkReactivate}
                                                disabled={bulkActionLoading}
                                            >
                                                {bulkActionLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <PlayCircle className="h-4 w-4 mr-1" />
                                                        Reactivate
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="outline"
                                                className="text-orange-500 border-orange-500 hover:bg-orange-50"
                                                onClick={handleBulkIdle}
                                                disabled={bulkActionLoading}
                                            >
                                                {bulkActionLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <PauseCircle className="h-4 w-4 mr-1" />
                                                        Set Idle
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="destructive"
                                                onClick={handleBulkClose}
                                                disabled={bulkActionLoading}
                                            >
                                                {bulkActionLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <Ban className="h-4 w-4 mr-1" />
                                                        Close
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="ghost"
                                                onClick={() => setSelectedUsers([])}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                <ScrollArea className="h-[500px]">
                                    <table className="w-full">
                                        <thead className="sticky top-0 bg-background">
                                            <tr className="border-b">
                                                <th className="text-left p-3 font-medium w-10">
                                                    <Checkbox 
                                                        checked={selectedUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                                                        onCheckedChange={handleSelectAll}
                                                        aria-label="Select all users"
                                                    />
                                                </th>
                                                <th className="text-left p-3 font-medium">User</th>
                                                <th className="text-left p-3 font-medium">Tier</th>
                                                <th className="text-left p-3 font-medium">Credits</th>
                                                <th className="text-left p-3 font-medium">Books</th>
                                                <th className="text-left p-3 font-medium">Status</th>
                                                <th className="text-left p-3 font-medium">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map(user => (
                                                <tr key={user.id} className={`border-b hover:bg-muted/50 ${selectedUsers.includes(user.id) ? 'bg-muted/30' : ''}`}>
                                                    <td className="p-3">
                                                        <Checkbox 
                                                            checked={selectedUsers.includes(user.id)}
                                                            onCheckedChange={() => handleSelectUser(user.id)}
                                                            aria-label={`Select ${user.name}`}
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <div>
                                                            <p className="font-medium">{user.name}</p>
                                                            <p className="text-xs text-muted-foreground">{user.email}</p>
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex flex-col gap-1">
                                                            <Badge variant={user.subscription_tier === 'free' ? 'secondary' : 'default'}>
                                                                {user.subscription_tier || 'free'}
                                                            </Badge>
                                                            {user.is_demo && (
                                                                <div className="flex items-center gap-1">
                                                                    <Badge className={isDemoExpired(user) ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-amber-500/10 text-amber-600 border-amber-500/20'}>
                                                                        <Star className="h-3 w-3 mr-1" />
                                                                        Demo
                                                                    </Badge>
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {formatExpiration(user.demo_expires_at)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3">{user.credits || 0}</td>
                                                    <td className="p-3">{user.book_count || 0}</td>
                                                    <td className="p-3">
                                                        {/* Account Status Badge */}
                                                        {user.account_status === 'closed' ? (
                                                            <Badge variant="destructive">Closed</Badge>
                                                        ) : user.account_status === 'idle' ? (
                                                            <Badge variant="outline" className="text-orange-500 border-orange-500">Idle</Badge>
                                                        ) : (
                                                            <Badge variant="default" className="bg-green-500">Active</Badge>
                                                        )}
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex gap-1 flex-wrap">
                                                            <Button size="sm" variant="outline" onClick={() => handleAddCredits(user.id)} title="Add Credits">
                                                                <Plus className="h-3 w-3 mr-1" />
                                                                Credits
                                                            </Button>
                                                            {user.is_demo && (
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="outline"
                                                                    onClick={() => handleEditDemo(user)}
                                                                    title="Edit Demo Duration"
                                                                    data-testid={`edit-demo-${user.id}`}
                                                                >
                                                                    <Calendar className="h-3 w-3 mr-1" />
                                                                    Edit
                                                                </Button>
                                                            )}
                                                            <Button 
                                                                size="sm" 
                                                                variant={user.is_demo ? "destructive" : "outline"}
                                                                onClick={() => handleToggleDemo(user.id, user.is_demo)}
                                                                title={user.is_demo ? "Revoke Demo" : "Grant Demo"}
                                                                data-testid={`demo-toggle-${user.id}`}
                                                            >
                                                                {user.is_demo ? (
                                                                    <>
                                                                        <XCircle className="h-3 w-3 mr-1" />
                                                                        Revoke
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Gift className="h-3 w-3 mr-1" />
                                                                        Demo
                                                                    </>
                                                                )}
                                                            </Button>
                                                            {/* Account Status Actions */}
                                                            {user.account_status === 'closed' || user.account_status === 'idle' ? (
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="outline"
                                                                    className="text-green-600 border-green-600 hover:bg-green-50"
                                                                    onClick={() => handleReactivateAccount(user.id, user.name)}
                                                                    title="Reactivate Account"
                                                                >
                                                                    <PlayCircle className="h-3 w-3 mr-1" />
                                                                    Reactivate
                                                                </Button>
                                                            ) : (
                                                                <>
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="outline"
                                                                        className="text-orange-500 border-orange-500 hover:bg-orange-50"
                                                                        onClick={() => handleSetIdle(user.id, user.name)}
                                                                        title="Set Idle (Payment Issue)"
                                                                    >
                                                                        <PauseCircle className="h-3 w-3 mr-1" />
                                                                        Idle
                                                                    </Button>
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="destructive"
                                                                        onClick={() => handleCloseAccount(user.id, user.name)}
                                                                        title="Close Account"
                                                                    >
                                                                        <Ban className="h-3 w-3 mr-1" />
                                                                        Close
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </ScrollArea>
                            </CardContent>
                        </Card>

                        {/* Create Account Modal */}
                        {showCreateModal && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
                                <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <UserPlus className="h-5 w-5 text-accent" />
                                            Create New Account
                                        </CardTitle>
                                        <CardDescription>
                                            Create a regular account or a demo account with full feature access.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div>
                                            <Label>Email *</Label>
                                            <Input
                                                type="email"
                                                value={createAccountData.email}
                                                onChange={e => setCreateAccountData({...createAccountData, email: e.target.value})}
                                                placeholder="user@example.com"
                                                data-testid="create-email-input"
                                            />
                                        </div>
                                        <div>
                                            <Label>Name *</Label>
                                            <Input
                                                value={createAccountData.name}
                                                onChange={e => setCreateAccountData({...createAccountData, name: e.target.value})}
                                                placeholder="John Doe"
                                                data-testid="create-name-input"
                                            />
                                        </div>
                                        <div>
                                            <Label>Password *</Label>
                                            <Input
                                                type="password"
                                                value={createAccountData.password}
                                                onChange={e => setCreateAccountData({...createAccountData, password: e.target.value})}
                                                placeholder="••••••••"
                                                data-testid="create-password-input"
                                            />
                                        </div>
                                        <div>
                                            <Label>Starting Credits</Label>
                                            <Input
                                                type="number"
                                                value={createAccountData.credits}
                                                onChange={e => setCreateAccountData({...createAccountData, credits: parseInt(e.target.value) || 0})}
                                                min={0}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="is_demo"
                                                checked={createAccountData.is_demo}
                                                onChange={e => setCreateAccountData({...createAccountData, is_demo: e.target.checked})}
                                                className="rounded"
                                            />
                                            <Label htmlFor="is_demo" className="cursor-pointer">
                                                Create as Demo Account (full feature access)
                                            </Label>
                                        </div>
                                        {createAccountData.is_demo && (
                                            <div>
                                                <Label>Demo Duration</Label>
                                                <Select 
                                                    value={createAccountData.demo_duration_days.toString()} 
                                                    onValueChange={v => setCreateAccountData({...createAccountData, demo_duration_days: parseInt(v)})}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="5">5 days</SelectItem>
                                                        <SelectItem value="14">14 days</SelectItem>
                                                        <SelectItem value="30">30 days</SelectItem>
                                                        <SelectItem value="90">90 days</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        <div className="flex gap-2 justify-end pt-2">
                                            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                                                Cancel
                                            </Button>
                                            <Button onClick={handleCreateAccount} disabled={createLoading} data-testid="create-account-submit">
                                                {createLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                ) : (
                                                    <UserPlus className="h-4 w-4 mr-1" />
                                                )}
                                                Create Account
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Edit Demo Duration Modal */}
                        {editDemoUser && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditDemoUser(null)}>
                                <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Calendar className="h-5 w-5 text-accent" />
                                            Edit Demo Duration
                                        </CardTitle>
                                        <CardDescription>
                                            Extend or adjust demo access for {editDemoUser.name} ({editDemoUser.email})
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <p className="text-sm">Current status: <span className={isDemoExpired(editDemoUser) ? 'text-red-500' : 'text-green-500'}>{formatExpiration(editDemoUser.demo_expires_at)}</span></p>
                                        </div>
                                        <div>
                                            <Label>New Duration (from today)</Label>
                                            <Select value={editDemoDuration} onValueChange={setEditDemoDuration}>
                                                <SelectTrigger data-testid="edit-demo-duration-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="5">5 days</SelectItem>
                                                    <SelectItem value="14">14 days</SelectItem>
                                                    <SelectItem value="30">30 days</SelectItem>
                                                    <SelectItem value="90">90 days</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Demo will expire on {new Date(Date.now() + parseInt(editDemoDuration) * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                            <Button variant="outline" onClick={() => setEditDemoUser(null)}>
                                                Cancel
                                            </Button>
                                            <Button onClick={handleUpdateDemoDuration} data-testid="update-demo-btn">
                                                <Calendar className="h-4 w-4 mr-1" />
                                                Update Duration
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Demo Duration Modal */}
                        {showDemoModal && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDemoModal(null)}>
                                <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Gift className="h-5 w-5 text-amber-500" />
                                            Grant Demo Access
                                        </CardTitle>
                                        <CardDescription>
                                            Demo users get full feature access with free-tier credits. They must purchase additional credits when needed.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div>
                                            <Label>Demo Duration</Label>
                                            <Select value={selectedDuration} onValueChange={setSelectedDuration}>
                                                <SelectTrigger className="mt-1" data-testid="demo-duration-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="5">5 days</SelectItem>
                                                    <SelectItem value="14">14 days</SelectItem>
                                                    <SelectItem value="30">30 days</SelectItem>
                                                    <SelectItem value="90">90 days</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                After expiration, user keeps their work and can upgrade to a paid plan
                                            </p>
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                            <Button variant="outline" onClick={() => setShowDemoModal(null)}>
                                                Cancel
                                            </Button>
                                            <Button 
                                                onClick={() => handleGrantDemo(showDemoModal, parseInt(selectedDuration))}
                                                data-testid="grant-demo-btn"
                                            >
                                                <Star className="h-4 w-4 mr-1" />
                                                Grant Demo
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </TabsContent>

                    {/* Affiliates Tab */}
                    <TabsContent value="affiliates">
                        <Card>
                            <CardHeader>
                                <CardTitle>Affiliate Management</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ScrollArea className="h-[500px]">
                                    <table className="w-full">
                                        <thead className="sticky top-0 bg-background">
                                            <tr className="border-b">
                                                <th className="text-left p-3 font-medium">Affiliate</th>
                                                <th className="text-left p-3 font-medium">Status</th>
                                                <th className="text-left p-3 font-medium">Referrals</th>
                                                <th className="text-left p-3 font-medium">Earnings</th>
                                                <th className="text-left p-3 font-medium">Pending</th>
                                                <th className="text-left p-3 font-medium">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {affiliates.map(affiliate => (
                                                <tr key={affiliate.id} className="border-b hover:bg-muted/50">
                                                    <td className="p-3">
                                                        <div>
                                                            <p className="font-medium">{affiliate.name}</p>
                                                            <p className="text-xs text-muted-foreground">{affiliate.email}</p>
                                                            <p className="text-xs text-accent">{affiliate.code}</p>
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        {affiliate.status === 'rejected' ? (
                                                            <Badge variant="outline" className="text-red-500 border-red-500">Rejected</Badge>
                                                        ) : affiliate.is_approved && !affiliate.is_active ? (
                                                            <Badge variant="outline" className="text-orange-500 border-orange-500">Revoked</Badge>
                                                        ) : affiliate.is_approved && affiliate.is_active ? (
                                                            <Badge variant="default" className="bg-green-500">Active</Badge>
                                                        ) : (
                                                            <Badge variant="destructive">Pending</Badge>
                                                        )}
                                                    </td>
                                                    <td className="p-3">{affiliate.total_referrals || 0}</td>
                                                    <td className="p-3">${(affiliate.total_earnings || 0).toFixed(2)}</td>
                                                    <td className="p-3">${(affiliate.pending_payout || 0).toFixed(2)}</td>
                                                    <td className="p-3">
                                                        <div className="flex gap-1 flex-wrap">
                                                            {/* Pending affiliates: Approve or Reject */}
                                                            {!affiliate.is_approved && affiliate.status !== 'rejected' && (
                                                                <>
                                                                    <Button 
                                                                        size="sm" 
                                                                        onClick={() => handleApproveAffiliate(affiliate.id)}
                                                                        className="bg-green-600 hover:bg-green-700"
                                                                    >
                                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                                        Approve
                                                                    </Button>
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="destructive"
                                                                        onClick={() => handleRejectAffiliate(affiliate.id)}
                                                                    >
                                                                        <XCircle className="h-3 w-3 mr-1" />
                                                                        Reject
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {/* Active affiliates: Revoke and Message */}
                                                            {affiliate.is_approved && affiliate.is_active && (
                                                                <>
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="outline"
                                                                        onClick={() => setMessageAffiliate(affiliate)}
                                                                    >
                                                                        <Mail className="h-3 w-3 mr-1" />
                                                                        Message
                                                                    </Button>
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="destructive"
                                                                        onClick={() => handleRevokeAffiliate(affiliate.id)}
                                                                    >
                                                                        <XCircle className="h-3 w-3 mr-1" />
                                                                        Revoke
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {/* Rejected status */}
                                                            {affiliate.status === 'rejected' && (
                                                                <Badge variant="outline" className="text-muted-foreground">Rejected</Badge>
                                                            )}
                                                            {/* Revoked status */}
                                                            {affiliate.is_approved && !affiliate.is_active && (
                                                                <Badge variant="outline" className="text-muted-foreground">Revoked</Badge>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                        
                        {/* Message Affiliate Modal */}
                        {messageAffiliate && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                <Card className="w-full max-w-lg">
                                    <CardHeader>
                                        <CardTitle>Message Affiliate</CardTitle>
                                        <CardDescription>
                                            Send a message to {messageAffiliate.name} ({messageAffiliate.email})
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div>
                                            <Label>Subject</Label>
                                            <Input
                                                value={messageSubject}
                                                onChange={(e) => setMessageSubject(e.target.value)}
                                                placeholder="Enter email subject"
                                            />
                                        </div>
                                        <div>
                                            <Label>Message</Label>
                                            <textarea
                                                className="w-full min-h-[150px] p-3 border rounded-md text-sm"
                                                value={messageContent}
                                                onChange={(e) => setMessageContent(e.target.value)}
                                                placeholder="Enter your message to the affiliate..."
                                            />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button 
                                                variant="outline" 
                                                onClick={() => {
                                                    setMessageAffiliate(null);
                                                    setMessageSubject('');
                                                    setMessageContent('');
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button 
                                                onClick={handleSendAffiliateMessage}
                                                disabled={messageSending}
                                            >
                                                {messageSending ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    'Send Message'
                                                )}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </TabsContent>

                    {/* Promo Codes Tab */}
                    <TabsContent value="promo">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Ticket className="h-5 w-5" />
                                        Promo Codes
                                    </CardTitle>
                                    <CardDescription>Create and manage promotional discount codes</CardDescription>
                                </div>
                                <Button onClick={() => setShowGenerateModal(true)} className="btn-primary">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Generate Code
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {/* Stats */}
                                <div className="grid grid-cols-3 gap-4 mb-6">
                                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold">{promoStats.total}</p>
                                        <p className="text-sm text-muted-foreground">Total Codes</p>
                                    </div>
                                    <div className="bg-green-500/10 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-green-600">{promoStats.active}</p>
                                        <p className="text-sm text-muted-foreground">Available</p>
                                    </div>
                                    <div className="bg-blue-500/10 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-blue-600">{promoStats.used}</p>
                                        <p className="text-sm text-muted-foreground">Redeemed</p>
                                    </div>
                                </div>

                                {/* Filter */}
                                <div className="flex gap-2 mb-4">
                                    <Select value={promoFilter} onValueChange={(v) => { setPromoFilter(v); loadPromoCodes(v); }}>
                                        <SelectTrigger className="w-40">
                                            <SelectValue placeholder="Filter" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Codes</SelectItem>
                                            <SelectItem value="active">Available</SelectItem>
                                            <SelectItem value="used">Redeemed</SelectItem>
                                            <SelectItem value="expired">Expired</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button variant="outline" size="sm" onClick={() => loadPromoCodes(promoFilter)}>
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>

                                {/* Promo Codes Table */}
                                {promoLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                    </div>
                                ) : promoCodes.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No promo codes found</p>
                                        <p className="text-sm">Click "Generate Code" to create your first promo code</p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[400px]">
                                        <table className="w-full">
                                            <thead className="sticky top-0 bg-background">
                                                <tr className="border-b text-left">
                                                    <th className="pb-2 font-medium">Code</th>
                                                    <th className="pb-2 font-medium">Type</th>
                                                    <th className="pb-2 font-medium">Status</th>
                                                    <th className="pb-2 font-medium">Used By</th>
                                                    <th className="pb-2 font-medium">Created</th>
                                                    <th className="pb-2 font-medium">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {promoCodes.map((promo) => (
                                                    <tr key={promo.code} className="border-b hover:bg-muted/50">
                                                        <td className="py-3">
                                                            <div className="flex items-center gap-2">
                                                                <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                                                                    {promo.code}
                                                                </code>
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="sm" 
                                                                    onClick={() => copyToClipboard(promo.code)}
                                                                >
                                                                    <Copy className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                        <td className="py-3">
                                                            <div>
                                                                <p className="font-medium text-sm">{promo.discount_percent}% off {promo.plan}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {promo.duration_months}mo + {promo.monthly_credits} credits/mo
                                                                </p>
                                                            </div>
                                                        </td>
                                                        <td className="py-3">
                                                            {promo.used_at ? (
                                                                <Badge variant="secondary">Redeemed</Badge>
                                                            ) : !promo.is_active ? (
                                                                <Badge variant="destructive">Inactive</Badge>
                                                            ) : (
                                                                <Badge className="bg-green-500">Available</Badge>
                                                            )}
                                                        </td>
                                                        <td className="py-3 text-sm">
                                                            {promo.used_by_email || '-'}
                                                        </td>
                                                        <td className="py-3 text-sm text-muted-foreground">
                                                            {new Date(promo.created_at).toLocaleDateString()}
                                                        </td>
                                                        <td className="py-3">
                                                            {!promo.used_at && (
                                                                <div className="flex gap-1">
                                                                    {promo.is_active && (
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm"
                                                                            onClick={() => handleDeactivatePromo(promo.code)}
                                                                        >
                                                                            <Ban className="h-4 w-4 text-orange-500" />
                                                                        </Button>
                                                                    )}
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm"
                                                                        onClick={() => handleDeletePromo(promo.code)}
                                                                    >
                                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>

                        {/* Generate Promo Code Modal */}
                        {showGenerateModal && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                <Card className="w-full max-w-md">
                                    <CardHeader>
                                        <CardTitle>Generate Promo Code</CardTitle>
                                        <CardDescription>Create new promotional discount codes</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Promo Type</Label>
                                            <Select 
                                                value={generatePromoData.template_id} 
                                                onValueChange={(v) => setGeneratePromoData({...generatePromoData, template_id: v})}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.entries(PROMO_TEMPLATES).map(([key, template]) => (
                                                        <SelectItem key={key} value={key}>
                                                            {template.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                {PROMO_TEMPLATES[generatePromoData.template_id]?.description}
                                            </p>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label>Quantity</Label>
                                            <Input 
                                                type="number" 
                                                min="1" 
                                                max="100"
                                                value={generatePromoData.quantity}
                                                onChange={(e) => setGeneratePromoData({...generatePromoData, quantity: parseInt(e.target.value) || 1})}
                                            />
                                        </div>
                                        
                                        {generatePromoData.quantity === 1 && (
                                            <div className="space-y-2">
                                                <Label>Custom Code (optional)</Label>
                                                <Input 
                                                    placeholder="e.g., WELCOME2025"
                                                    value={generatePromoData.custom_code}
                                                    onChange={(e) => setGeneratePromoData({...generatePromoData, custom_code: e.target.value.toUpperCase()})}
                                                />
                                                <p className="text-xs text-muted-foreground">Leave blank to auto-generate</p>
                                            </div>
                                        )}
                                        
                                        <div className="space-y-2">
                                            <Label>Notes (optional)</Label>
                                            <Input 
                                                placeholder="e.g., For podcast listeners"
                                                value={generatePromoData.notes}
                                                onChange={(e) => setGeneratePromoData({...generatePromoData, notes: e.target.value})}
                                            />
                                        </div>
                                        
                                        <div className="flex gap-2 pt-4">
                                            <Button variant="outline" className="flex-1" onClick={() => setShowGenerateModal(false)}>
                                                Cancel
                                            </Button>
                                            <Button className="flex-1 btn-primary" onClick={handleGeneratePromo}>
                                                Generate {generatePromoData.quantity > 1 ? `${generatePromoData.quantity} Codes` : 'Code'}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </TabsContent>

                    {/* Payouts Tab */}
                    <TabsContent value="payouts">
                        <Card>
                            <CardHeader>
                                <CardTitle>Payout Requests</CardTitle>
                                <CardDescription>Review and process affiliate payout requests</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {payoutRequests.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No pending payout requests</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {payoutRequests.map(request => (
                                            <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg">
                                                <div>
                                                    <p className="font-medium">{request.affiliate_name}</p>
                                                    <p className="text-sm text-muted-foreground">{request.affiliate_email}</p>
                                                    <div className="flex items-center gap-4 mt-2 text-sm">
                                                        <span className="text-accent font-semibold">${request.amount.toFixed(2)}</span>
                                                        <span className="text-muted-foreground">{request.payout_method}</span>
                                                        <span className="text-muted-foreground">{request.payout_details}</span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="outline" onClick={() => handleRejectPayout(request.id)}>
                                                        <XCircle className="h-4 w-4 mr-1" />
                                                        Reject
                                                    </Button>
                                                    <Button size="sm" onClick={() => handleApprovePayout(request.id)}>
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                        Approve
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default AdminDashboard;
